import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { ChipItemData } from '@components/ChipList'
import alertsApi from '@modules/alerts/api'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { preparePendoSearchGroupingEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoEventType } from '@modules/pendo/types'
import savedSearchApi from '@modules/savedSearch/api'
import { SavedSearch } from '@modules/savedSearch/types'
import searchApi from '@modules/search/api'
import { FiltersSetup, MetaFilterTypes, ProfileType, SelectedFiltersMapByProfiles } from '@modules/search/types'
import TuneIcon from '@mui/icons-material/Tune'
import { Box, Button, Drawer, Typography } from '@mui/material'
import { useContactHistory } from '@shared/hooks/useContactHistory'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import { FiltersSection, ResultsSection, SearchSection } from './components'
import SearchByDate from './components/FiltersSection/SearchByDate'
import { SearchByEvent } from './components/FiltersSection/SearchByEvent'
import SearchByHistory from './components/FiltersSection/SearchByHistory'
import { ProfileTypeFilters } from './components/SearchSection/ProfileTypeFilters'
import { DEFAULT_PAGE_LIMIT, HISTORY_FILTER_OPTIONS } from './constants'
import { useSearchProfiles } from './hooks/useSearchProfiles'
import { useSearchState } from './hooks/useSearchState'
import { ContentSectionContainer, FilterSectionContainer, SearchInputSectionContainer, SearchPageInner } from './index.styles'

const Search = () => {
  const {
    selectedSortId,
    selectedGroupingId,
    metaFilters,
    page,
    scrollX,
    scrollY,
    searchString,
    selectedMetaFilter,
    expandedFilter,
    filtersSetup,
    selectedFiltersByProfiles,
    isSearchAllProfilesChecked,
    searchableProfilesMap,
    filterSectionExpandAllOptions,
    filterSectionOptionsSearch,
    selectedNewItemsFilter,
    setAppliedSavedSearchName,
    appliedSavedSearchName,
    savedSearchId,
    setSavedSearchId,
    handleClearSection,
    handleSubmitMetaFilter,
    setSelectedNewItemsFilter,
    setFiltersSetup,
    resetAll,
    setPage,
    setScrollX,
    setScrollY,
    handleSearch,
    handleSort,
    handleGroupSelected,
    toggleItemFilter,
    handleExpandFilter,
    handleSetSearchableProfiles,
    handleSetAllSearchableProfiles,
    setFilterSectionExpandAllOptions,
    setFilterSectionOptionsSearch,
    eventsFilterData,
    setEventsFilter,
    searchHistoryTypeId,
    setSearchHistoryTypeId,
  } = useSearchState()
  const { isDesktop } = useScreenDimensions()
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false)
  const queryClient = useQueryClient()
  const { trackPendoEvent } = usePendoTrackEvent()
  const { user } = useAuthContext()
  const { hasAccessToSavedSearch, hasAccessToEventFiltering } = useFeatureFlags()
  const { t } = useTranslation()
  const isFilterApplied = useMemo(
    () =>
      !!Object.values(selectedFiltersByProfiles as SelectedFiltersMapByProfiles).flatMap(values => Object.values(values)?.flat())
        ?.length ||
      (selectedMetaFilter && selectedMetaFilter.type !== MetaFilterTypes.DEFAULT) ||
      !!searchHistoryTypeId ||
      !!eventsFilterData?.includeIds?.length ||
      !!eventsFilterData?.excludeIds?.length,
    [selectedFiltersByProfiles, selectedMetaFilter, searchHistoryTypeId, eventsFilterData],
  )

  const {
    fetchCompanyIds = false,
    hasAttended,
    onlyMine = undefined,
    exclude = false,
  } = useMemo(() => {
    const historyFilterOption = HISTORY_FILTER_OPTIONS.find(option => option.id === searchHistoryTypeId)?.data

    return historyFilterOption
      ? { fetchCompanyIds: true, ...historyFilterOption }
      : { fetchCompanyIds: false, onlyMine: undefined, exclude: undefined, hasAttended: undefined }
  }, [searchHistoryTypeId])

  const companyIds = useContactHistory(fetchCompanyIds, { onlyMine, hasAttended, exclude })

  const { data: savedSearches } = useQuery({
    queryKey: [savedSearchApi.getSavedSearches.key],
    queryFn: () => savedSearchApi.getSavedSearches.fn(),
    enabled: hasAccessToSavedSearch,
  })

  const userEventsOptions = useMemo(() => {
    const events = [...(user?.events ?? [])]?.sort(
      (eventPrev, eventNext) => new Date(eventNext.startDate).getTime() - new Date(eventPrev.startDate).getTime(),
    )

    return events.map(event => ({
      label: event.name,
      value: event.id,
      hidden: event.hidden,
    }))
  }, [user?.events])

  const mergeSearchableFilters = useCallback(
    (incomingFilters: FiltersSetup) => {
      setFiltersSetup((currentFiltersSetup: FiltersSetup) => {
        /** disabling all filters, so only those which came will be enabled */
        const initialState = Object.entries(currentFiltersSetup || {}).reduce<FiltersSetup>(
          (acc, [filter, data]) => ({ ...acc, [filter]: { ...data, total: 0, enabled: !isFilterApplied } }),
          {},
        )
        return Object.entries(incomingFilters || {}).reduce((acc, [filter, data]) => {
          if (acc[filter]) {
            acc[filter].total = data.total || 0
            acc[filter].facets = data.facets
            acc[filter].enabled = true
          } else {
            acc[filter] = {
              ...data,
              enabled: true,
            }
          }

          return acc
        }, initialState)
      })
    },
    [isFilterApplied, setFiltersSetup],
  )

  const onGroupingSelected = (id: number, withPageReset?: boolean) => {
    handleGroupSelected(id, withPageReset)
    const pendoTrackingEvent = preparePendoSearchGroupingEvent({
      groupingOptionId: id,
      user,
      eventType: PendoEventType.SearchPageGrouping,
    })

    trackPendoEvent(PendoEventType.SearchPageGrouping, pendoTrackingEvent)
  }

  const searchableEntities = useMemo(
    () =>
      Object.keys(searchableProfilesMap).filter(
        type => isSearchAllProfilesChecked || !!searchableProfilesMap[type as ProfileType],
      ) as ProfileType[],
    [searchableProfilesMap, isSearchAllProfilesChecked],
  )

  const isGroupByDisabled = useMemo(
    () => !(isSearchAllProfilesChecked || searchableProfilesMap[ProfileType.company]),
    [isSearchAllProfilesChecked, searchableProfilesMap],
  )

  const isSortingDisabled = useMemo(() => Boolean(selectedGroupingId || searchString), [searchString, selectedGroupingId])

  const { data: { eventsYears } = {} } = useQuery({
    queryKey: [searchApi.getMetaFiltersData.key],
    queryFn: () => searchApi.getMetaFiltersData.fn(),
  })

  const from = DEFAULT_PAGE_LIMIT * (page - 1)

  const selectedSavedSearch = useMemo(
    () => (savedSearchId ? savedSearches?.find(i => i.id === savedSearchId) : null),
    [savedSearches, savedSearchId],
  )

  const { data: { result: searchResult = [], total = 0, isResultGrouped = false } = {}, rawSearchParams } = useSearchProfiles({
    searchString,
    selectedFiltersByProfiles,
    from,
    selectedSavedSearch,
    searchableEntities,
    isSearchAllProfilesChecked,
    searchableProfilesMap,
    savedSearchId,
    selectedNewItemsFilter,
    selectedSortId,
    /** @ts-expect-error will be fixed soon */
    mergeSearchableFilters,
    metaFilters,
    selectedGroupingId,
    eventsFilterData,
    onSearchSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [alertsApi.fetchProfilesAlerts.key] })
    },
    companyHistoryFilter: {
      companyIds: fetchCompanyIds ? companyIds : undefined,
      exclude,
    },
  })

  useEffect(() => {
    if (!selectedSavedSearch?.newItemsCount && selectedNewItemsFilter) {
      setSelectedNewItemsFilter(false)
    }
    // eslint-disable-next-line
  }, [selectedSavedSearch?.newItemsCount])

  useEffect(() => {
    if (searchResult?.length && (scrollX || scrollY)) {
      window.scrollTo({
        top: scrollY,
        left: scrollX,
        behavior: 'smooth',
      })

      // We need to use setTimeout in this case due to Safari-only issue.
      // `window.scrollTo` works great in all other browsers, but not in Safari.
      // So that is why we are wrapping `window.scroll` into setTimeout here.
      setTimeout(() => {
        window.scroll({ top: scrollY, left: scrollX, behavior: 'smooth' })
      }, 10)
    }
  }, [searchResult?.length, scrollX, scrollY])

  const { data: searchResponse } = useQuery({
    queryKey: [searchApi.getFilters.key],
    queryFn: () =>
      searchApi.getFilters.fn({
        from: 0,
        size: 10,
        types: Object.keys(searchableProfilesMap).map(type => ({ type, isSelected: true })),
      }),
    enabled: !filtersSetup,
  })

  useEffect(() => {
    if (searchResponse) {
      const { facets } = searchResponse
      // @ts-expect-error type mismatch
      mergeSearchableFilters(facets)
    }
    // eslint-disable-next-line
  }, [searchResponse])

  const totalPages = useMemo(() => (total ? Math.ceil(total / DEFAULT_PAGE_LIMIT) : 0), [total])

  const onDeleteChip = useCallback(
    (chipItem: ChipItemData) => {
      if ((chipItem.metaData as { removeMetaFilter: boolean }).removeMetaFilter) {
        handleSubmitMetaFilter({
          type: MetaFilterTypes.DEFAULT,
          value: '',
        })
        return
      }
      if ((chipItem.metaData as { removeHistoryFilter: boolean }).removeHistoryFilter) {
        setSearchHistoryTypeId(undefined)
        return
      }

      if ((chipItem.metaData as { removeEventFilter: boolean }).removeEventFilter) {
        setEventsFilter({
          includeIds: [],
          excludeIds: [],
        })
        return
      }
      const { removeExcludedEventId } = chipItem.metaData as { removeExcludedEventId: number }
      if (removeExcludedEventId) {
        setEventsFilter({
          includeIds: eventsFilterData?.includeIds || [],
          excludeIds: eventsFilterData?.excludeIds?.filter(excludedId => excludedId !== removeExcludedEventId) || [],
        })
        return
      }
      const removedIncludedEventId = (chipItem.metaData as { removeIncludedEventId: number }).removeIncludedEventId
      if (removedIncludedEventId) {
        setEventsFilter({
          includeIds: eventsFilterData?.includeIds?.filter(includeId => includeId !== removedIncludedEventId) || [],
          excludeIds: eventsFilterData?.excludeIds || [],
        })
        return
      }

      const metadata = chipItem.metaData as { filterType: ProfileType; field: string }

      toggleItemFilter(metadata.filterType, metadata.field || '', chipItem.key, false)
    },
    [
      toggleItemFilter,
      handleSubmitMetaFilter,
      setSearchHistoryTypeId,
      setEventsFilter,
      eventsFilterData?.includeIds,
      eventsFilterData?.excludeIds,
    ],
  )

  const setSavedSearch = (savedSearch: Nullable<SavedSearch>) => {
    setSavedSearchId(savedSearch?.id ?? null)
    setAppliedSavedSearchName(savedSearch?.name ?? '')
  }

  const { mutate: markViewedProfiles } = useMutation({
    mutationFn: savedSearchApi.markViewedProfiles.fn,
    onSuccess: (_: unknown, data) => {
      queryClient.setQueryData<(string | number)[]>([savedSearchApi.runSavedSearch.key, savedSearchId], (oldData = []) => {
        return [...oldData, ...data.profileIds]
      })
      queryClient.invalidateQueries({ queryKey: [savedSearchApi.runSavedSearch.key, savedSearchId] })
      queryClient.invalidateQueries({ queryKey: [savedSearchApi.getSavedSearches.key] })
    },
  })
  const onProfileNavigate = useCallback(
    (id: string | number) => {
      if (savedSearchId) {
        markViewedProfiles({ id: savedSearchId, profileIds: [id] })
      }
    },
    [savedSearchId, markViewedProfiles],
  )

  const Filters = (
    <FilterSectionContainer>
      {!isDesktop && (
        <ProfileTypeFilters
          accordionView
          handleSetSearchableProfiles={handleSetSearchableProfiles}
          handleSetAllSearchableProfiles={handleSetAllSearchableProfiles}
          isAllProfilesEnabled={isSearchAllProfilesChecked}
          searchableProfilesMap={searchableProfilesMap}
          selectedFiltersByProfiles={selectedFiltersByProfiles}
        />
      )}

      <SearchByHistory
        searchHistoryTypeId={searchHistoryTypeId || 0}
        setSearchHistoryTypeId={setSearchHistoryTypeId}
        prefix="search_by_history"
        isFilterExpanded={expandedFilter?.search_by_history}
        onExpand={isExpanded => handleExpandFilter('search_by_history', 'search_by_history', isExpanded)}
      />
      {hasAccessToEventFiltering && (
        <SearchByEvent
          prefix="search_by_event"
          isFilterExpanded={expandedFilter?.search_by_event}
          onExpand={isExpanded => handleExpandFilter('search_by_event', 'search_by_event', isExpanded)}
          eventsFilterData={eventsFilterData}
          onSubmit={setEventsFilter}
          eventsOptions={userEventsOptions}
        />
      )}
      {eventsYears && (
        <SearchByDate
          selectedFilter={selectedMetaFilter}
          handleSelectMetaFilter={handleSubmitMetaFilter}
          eventsDatesRange={eventsYears}
          prefix="meta_filter"
          isFilterExpanded={expandedFilter?.meta_filter}
          onExpand={isExpanded => handleExpandFilter('meta_filter', 'meta_filter', isExpanded)}
        />
      )}
      <FiltersSection
        filtersSetup={filtersSetup}
        searchableEntitiesMap={searchableProfilesMap}
        isAllProfilesEnabled={isSearchAllProfilesChecked}
        toggleItemFilter={toggleItemFilter}
        selectedFiltersByProfiles={selectedFiltersByProfiles}
        handleClearSection={handleClearSection}
        expandedFilter={expandedFilter}
        handleExpandFilter={handleExpandFilter}
        filterSectionExpandAllOptions={filterSectionExpandAllOptions}
        filterSectionOptionsSearch={filterSectionOptionsSearch}
        setFilterSectionExpandAllOptions={setFilterSectionExpandAllOptions}
        setFilterSectionOptionsSearch={setFilterSectionOptionsSearch}
      />
    </FilterSectionContainer>
  )

  const toggleFiltersDrawer = () => {
    setIsFiltersDrawerOpen(isOpen => !isOpen)
  }

  return (
    <SearchPageInner>
      {isDesktop && Filters}
      <ContentSectionContainer>
        <SearchInputSectionContainer>
          <SearchSection
            toggleFiltersDrawer={!isDesktop ? toggleFiltersDrawer : undefined}
            setSavedSearch={setSavedSearch}
            appliedSavedSearchName={appliedSavedSearchName}
            searchString={searchString}
            handleSearchClick={handleSearch}
            savedSearches={savedSearches}
            handleResetAll={resetAll}
            selectedFiltersByProfiles={selectedFiltersByProfiles}
            handleSetSearchableProfiles={handleSetSearchableProfiles}
            searchableProfilesMap={searchableProfilesMap}
            handleSetAllSearchableProfiles={handleSetAllSearchableProfiles}
            isAllProfilesEnabled={isSearchAllProfilesChecked}
          />
        </SearchInputSectionContainer>

        <ResultsSection
          savedSearchId={savedSearchId ?? undefined}
          onProfileNavigate={onProfileNavigate}
          appliedSavedSearchName={appliedSavedSearchName}
          isFilterApplied={isFilterApplied}
          isGroupByDisabled={isGroupByDisabled}
          selectedFiltersByProfiles={selectedFiltersByProfiles}
          searchString={searchString}
          onDeleteChip={onDeleteChip}
          total={total}
          resultProfiles={searchResult}
          totalPages={totalPages}
          page={page}
          selectedSortId={selectedSortId}
          sortDisabled={isSortingDisabled}
          onSort={handleSort}
          onGroup={onGroupingSelected}
          selectedGroupingId={selectedGroupingId}
          setPage={setPage}
          setScrollX={setScrollX}
          setScrollY={setScrollY}
          selectedMetaFilter={selectedMetaFilter}
          isResultGrouped={isResultGrouped}
          userEventsOptions={userEventsOptions}
          eventsFilterData={eventsFilterData}
          searchBody={rawSearchParams}
          searchHistoryTypeId={searchHistoryTypeId}
          selectedNewItemsFilter={selectedNewItemsFilter}
          showNewItemsFilter={!!selectedSavedSearch?.newItemsCount}
          handleSelectNewItemsFilter={setSelectedNewItemsFilter}
        />
      </ContentSectionContainer>

      {!isDesktop && (
        <Drawer anchor="left" open={isFiltersDrawerOpen} onClose={toggleFiltersDrawer} ModalProps={{ keepMounted: false }}>
          <Box
            p="10px"
            mb="10px"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            borderBottom={`1px solid ${colors.commonLightGrey}`}
          >
            <Box display="flex" alignItems="center" gap="4px">
              <TuneIcon color="primary" />
              <Typography variant="caption">{t(translationKeys.SEARCH_FILTERS_TITLE)}</Typography>
            </Box>
          </Box>

          <Box padding="10px 0 10px 10px">{Filters}</Box>

          <Box px="10px" mb="10px" alignSelf="flex-end">
            <Button
              id="search-close_filters_button"
              onClick={toggleFiltersDrawer}
              data-testid="close_filters_button"
              variant="contained"
              size="small"
            >
              {t(translationKeys.COMMON_CLOSE)}
            </Button>
          </Box>
        </Drawer>
      )}
    </SearchPageInner>
  )
}

export default Search
